import React, { useEffect, useState, useCallback } from 'react';
import { dashboard } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// ── PLATFORM CONFIG ──────────────────────────────────────────
const PLATFORM_COLORS = {
  instagram: '#e1306c',
  facebook:  '#1877f2',
  linkedin:  '#0a66c2',
  tiktok:    '#ff0050',
  x:         '#9b9bb4',
};

function fmt(n) {
  if (n == null) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000)    return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString();
}

// ── DATE RANGE PICKER ─────────────────────────────────────────
const PRESETS = [
  { label: '7D',   days: 7 },
  { label: '30D',  days: 30 },
  { label: '90D',  days: 90 },
  { label: '12M',  days: 365 },
  { label: 'YTD',  ytd: true },
  { label: 'Custom', custom: true },
];

function buildRange(preset) {
  const end   = new Date();
  let   start = new Date();
  if (preset.ytd) {
    start = new Date(end.getFullYear(), 0, 1);
  } else {
    start = new Date(Date.now() - preset.days * 24 * 60 * 60 * 1000);
  }
  return {
    start: start.toISOString().slice(0, 10),
    end:   end.toISOString().slice(0, 10),
  };
}

function DateRangePicker({ range, onChange }) {
  const [active,    setActive]    = useState('30D');
  const [custom,    setCustom]    = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');

  const selectPreset = (p) => {
    if (p.custom) {
      setCustom(true);
      setActive('Custom');
      return;
    }
    setCustom(false);
    setActive(p.label);
    onChange(buildRange(p));
  };

  const applyCustom = () => {
    if (customStart && customEnd) {
      onChange({ start: customStart, end: customEnd });
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {PRESETS.map(p => (
        <button
          key={p.label}
          onClick={() => selectPreset(p)}
          style={{
            padding: '5px 12px',
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            letterSpacing: '1px',
            border: '1px solid',
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'all 0.15s',
            background: active === p.label ? 'var(--accent-purple)' : 'transparent',
            borderColor: active === p.label ? 'var(--accent-purple)' : 'var(--border)',
            color: active === p.label ? '#fff' : 'var(--text-dim)',
          }}
        >
          {p.label}
        </button>
      ))}
      {custom && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            style={{ fontSize: 11, padding: '4px 8px', background: 'var(--bg-elevated)',
                     border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)' }}
          />
          <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>→</span>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            style={{ fontSize: 11, padding: '4px 8px', background: 'var(--bg-elevated)',
                     border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)' }}
          />
          <button
            onClick={applyCustom}
            style={{ padding: '5px 10px', fontSize: 11, fontFamily: 'var(--font-mono)',
                     background: 'var(--accent-purple)', color: '#fff', border: 'none',
                     borderRadius: 4, cursor: 'pointer' }}
          >
            GO
          </button>
        </div>
      )}
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────
function StatCard({ label, value, delta, sub, color }) {
  const isUp = delta > 0;
  return (
    <div className="card" style={{ flex: 1, minWidth: 160 }}>
      <div className="label">{label}</div>
      <div className="stat-value" style={{ color: color || 'var(--text-primary)' }}>
        {value ?? '—'}
      </div>
      {delta !== undefined && delta !== null && (
        <div className={isUp ? 'stat-delta-up' : 'stat-delta-down'} style={{ marginTop: 6 }}>
          {isUp ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}% vs prior period
        </div>
      )}
      {sub && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── PLATFORM CARD ─────────────────────────────────────────────
function PlatformCard({ p }) {
  const color = PLATFORM_COLORS[p.platform] || 'var(--accent-purple)';
  return (
    <div className="card" style={{ flex: '1 1 180px', minWidth: 160 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{
          fontSize: 10, fontFamily: 'var(--font-mono)', padding: '2px 7px',
          borderRadius: 3, textTransform: 'uppercase', letterSpacing: '1px',
          background: color + '18', color
        }}>
          {p.platform}
        </span>
        {p.followers > 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {fmt(p.followers)} followers
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(p.total_reach)}</div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>REACH</div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color }}>
            {Number(p.avg_engagement || 0).toFixed(1)}%
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>ENG</div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{p.total_posts}</div>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>POSTS</div>
        </div>
      </div>
    </div>
  );
}

// ── POST CARD ─────────────────────────────────────────────────
function PostCard({ post, rank }) {
  return (
    <div className="card fade-in" style={{
      display: 'flex', gap: 14, alignItems: 'flex-start',
      padding: '14px 16px', marginBottom: 8
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700,
        color: rank === 1 ? 'var(--accent-green)' : 'var(--text-dim)',
        minWidth: 28
      }}>
        #{rank}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{
            fontSize: 10, fontFamily: 'var(--font-mono)', padding: '2px 6px',
            borderRadius: 3, background: (PLATFORM_COLORS[post.platform] || '#888') + '22',
            color: PLATFORM_COLORS[post.platform] || 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '1px'
          }}>
            {post.platform}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            {post.post_type}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>
            {post.posted_at ? new Date(post.posted_at).toLocaleDateString() : ''}
          </span>
        </div>
        <div style={{
          fontSize: 13, color: 'var(--text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', lineHeight: 1.5
        }}>
          {post.caption || '(no caption)'}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 700, color: 'var(--accent-green)' }}>
          {post.engagement_rate}%
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>ENG RATE</div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
          {fmt(post.reach)} reach
        </div>
      </div>
    </div>
  );
}

// ── TOOLTIP ────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        padding: '10px 14px', borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 11
      }}>
        <div style={{ color: 'var(--text-dim)', marginBottom: 4 }}>Week {label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color }}>
            {p.name}: {typeof p.value === 'number' ? p.value.toFixed(2) : p.value}
            {p.dataKey !== 'patterns_confirmed' ? '%' : ''}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// ── PIVOT PLATFORM TRENDS DATA ────────────────────────────────
function pivotPlatformTrends(rows) {
  const map = {};
  (Array.isArray(rows) ? rows : []).forEach(row => {
    const key = `W${String(row.week_number).padStart(2,'0')}`;
    if (!map[key]) map[key] = { week: key };
    map[key][row.platform] = parseFloat(row.avg_engagement) || 0;
  });
  return Object.values(map).sort((a, b) => a.week.localeCompare(b.week));
}

// ── DASHBOARD ─────────────────────────────────────────────────
export default function Dashboard() {
  const defaultRange = buildRange(PRESETS[1]); // 30D default
  const [range,      setRange]      = useState(defaultRange);
  const [overview,   setOverview]   = useState(null);
  const [posts,      setPosts]      = useState([]);
  const [trends,     setTrends]     = useState([]);
  const [platTrends, setPlatTrends] = useState([]);
  const [platforms,  setPlatforms]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [syncing,    setSyncing]    = useState(false);
  const [syncMsg,    setSyncMsg]    = useState('');

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const [ov, tp, tr, pt, pl] = await Promise.all([
        dashboard.overview(r),
        dashboard.topPosts({ ...r, limit: 5 }),
        dashboard.trends(r),
        dashboard.platformTrends(r),
        dashboard.platforms(r),
      ]);
      setOverview(ov.data);
      setPosts(Array.isArray(tp.data) ? tp.data : []);
      setTrends(Array.isArray(tr.data) ? tr.data : []);
      setPlatTrends(pivotPlatformTrends(pt.data));
      setPlatforms(Array.isArray(pl.data) ? pl.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [load, range]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await dashboard.sync();
      setSyncMsg(res.data.message);
      setTimeout(() => { setSyncMsg(''); load(range); }, 3000);
    } catch {
      setSyncMsg('Sync failed. Check your connections.');
    } finally {
      setSyncing(false);
    }
  };

  const cw = overview?.currentWeek || {};
  const lw = overview?.lastWeek    || {};

  const reachDelta = lw.total_reach > 0
    ? ((cw.total_reach - lw.total_reach) / lw.total_reach * 100) : null;
  const engDelta = lw.avg_engagement_rate > 0
    ? ((cw.avg_engagement_rate - lw.avg_engagement_rate) / lw.avg_engagement_rate * 100) : null;

  const activePlatforms = [...new Set(platTrends.flatMap(r => Object.keys(r).filter(k => k !== 'week')))];

  return (
    <div style={{ padding: 32 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)',
                        letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 6 }}>
            Intelligence Overview
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--text-primary)' }}>
            Performance Dashboard
          </h1>
          {overview?.weeksOfData > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>
              {overview.weeksOfData} weeks of compounding data
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {syncMsg && (
              <div style={{ fontSize: 12, color: 'var(--accent-green)', fontFamily: 'var(--font-mono)' }}>
                {syncMsg}
              </div>
            )}
            <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
              {syncing ? <span className="pulsing">SYNCING...</span> : '↻ Sync Now'}
            </button>
          </div>
          <DateRangePicker range={range} onChange={r => setRange(r)} />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
                      fontSize: 11, letterSpacing: '2px' }} className="pulsing">
          LOADING INTELLIGENCE...
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
            <StatCard label="Total Reach"       value={fmt(cw.total_reach)}       delta={reachDelta} />
            <StatCard label="Avg Engagement"    value={`${Number(cw.avg_engagement_rate||0).toFixed(2)}%`}
                                                delta={engDelta} color="var(--accent-purple)" />
            <StatCard label="Posts Analysed"    value={cw.total_posts || 0}        sub="in this period" />
            <StatCard label="Confirmed Patterns" value={overview?.confirmedPatterns || 0}
                                                color="var(--accent-green)" sub="medium + high confidence" />
          </div>

          {/* Platform overview */}
          {platforms.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div className="label" style={{ marginBottom: 12 }}>Platform Breakdown</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {platforms.map(p => <PlatformCard key={p.platform} p={p} />)}
              </div>
            </div>
          )}

          {/* Per-platform engagement area chart */}
          {platTrends.length > 0 && (
            <div className="card" style={{ marginBottom: 28 }}>
              <div className="label" style={{ marginBottom: 4 }}>Engagement Rate by Platform</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 16 }}>
                Weekly avg engagement % per platform
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={platTrends} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <defs>
                    {activePlatforms.map(p => (
                      <linearGradient key={p} id={`grad-${p}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={PLATFORM_COLORS[p] || '#888'} stopOpacity={0.15} />
                        <stop offset="95%" stopColor={PLATFORM_COLORS[p] || '#888'} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="week"
                    tick={{ fill: 'var(--text-dim)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
                    tickLine={false} axisLine={{ stroke: 'var(--border)' }}
                  />
                  <YAxis
                    tick={{ fill: 'var(--text-dim)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
                    tickLine={false} axisLine={false}
                    tickFormatter={v => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                      borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 11
                    }}
                    formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, fontFamily: 'var(--font-mono)', paddingTop: 8 }}
                  />
                  {activePlatforms.map(p => (
                    <Area
                      key={p}
                      type="monotone"
                      dataKey={p}
                      name={p}
                      stroke={PLATFORM_COLORS[p] || '#888'}
                      strokeWidth={2}
                      fill={`url(#grad-${p})`}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Weekly rollup line chart */}
          {trends.length > 0 && (
            <div className="card" style={{ marginBottom: 28 }}>
              <div className="label" style={{ marginBottom: 16 }}>Weekly Engagement & Pattern Trends</div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={trends} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="week_number"
                    tick={{ fill: 'var(--text-dim)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
                    tickLine={false} axisLine={{ stroke: 'var(--border)' }}
                  />
                  <YAxis
                    tick={{ fill: 'var(--text-dim)', fontSize: 10, fontFamily: 'var(--font-mono)' }}
                    tickLine={false} axisLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="avg_engagement_rate" name="Engagement %"
                    stroke="var(--accent-purple)" strokeWidth={2}
                    dot={{ fill: 'var(--accent-purple)', r: 3 }} activeDot={{ r: 5 }}
                  />
                  <Line type="monotone" dataKey="patterns_confirmed" name="Patterns"
                    stroke="var(--accent-green)" strokeWidth={1.5}
                    strokeDasharray="4 4" dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top Posts */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="label" style={{ marginBottom: 0 }}>Top Posts This Period</div>
            </div>
            {posts.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>📡</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '2px' }}>
                  NO DATA FOR THIS PERIOD
                </div>
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  Try a wider date range or connect a platform in Settings, then Sync Now.
                </div>
                <Link
                  to="/settings"
                  className="btn btn-primary"
                  style={{ display: 'inline-flex', marginTop: 16, textDecoration: 'none' }}
                  title="Open Settings to connect an Instagram Professional account or Facebook Page"
                >
                  Connect a platform →
                </Link>
              </div>
            ) : (
              posts.map((p, i) => <PostCard key={p.id} post={p} rank={i + 1} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
